import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { fmtDate, fmtNum } from "../../lib/format";
import { useLang } from "../../lib/i18n";
import { PageTitle, StatusBadge, Spinner } from "../../components/Shell";
import { toast } from "sonner";
import { X } from "lucide-react";

const FLAGS = ["financing", "accounting", "agm", "ai", "whatsapp", "procurement", "multi_branch", "advanced_reports"];

export default function Tenants() {
  const [tenants, setTenants] = useState(null);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();
  const { lang, t } = useLang();

  const load = () => api.get("/platform/tenants").then((r) => setTenants(r.data.tenants)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (t, status) => {
    try {
      await api.put(`/platform/tenants/${t.tenant_id}/status`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const toggleFlag = async (flag) => {
    try {
      const res = await api.put(`/platform/tenants/${selected.tenant_id}/flags`, { flags: { [flag]: !selected.feature_flags[flag] } });
      setSelected(res.data);
      load();
    } catch {
      toast.error("Error");
    }
  };

  if (!tenants) return <Spinner />;

  return (
    <div data-testid="platform-tenants">
      <PageTitle title={lang === "bm" ? "Penyewa (Koperasi)" : "Tenants (Cooperatives)"} sub={`${tenants.length} koperasi berdaftar`} />
      <div className="bg-card rounded-xl border border-brand-deep/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="border-b border-brand-deep/10 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-5 py-3">Koperasi</th>
              <th className="px-5 py-3">Pelan</th>
              <th className="px-5 py-3">{t("common.status")}</th>
              <th className="px-5 py-3 text-right">Ahli</th>
              <th className="px-5 py-3">{t("common.date")}</th>
              <th className="px-5 py-3">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.tenant_id} className="border-b border-brand-deep/5 hover:bg-brand-light/50 transition-colors" data-testid={`tenant-row-${t.slug}`}>
                <td className="px-5 py-3">
                  <div className="font-medium text-brand-dark">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.slug}.kopera.app</div>
                </td>
                <td className="px-5 py-3 uppercase text-xs font-bold text-brand-deep">{t.plan}</td>
                <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
                <td className="px-5 py-3 text-right font-mono">{fmtNum(t.member_count)}{t.limits?.members > 0 ? ` / ${fmtNum(t.limits.members)}` : ""}</td>
                <td className="px-5 py-3 text-xs text-muted-foreground">{fmtDate(t.created_at)}</td>
                <td className="px-5 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`tenant-view-${t.slug}`} onClick={() => navigate(`/platform/tenants/${t.tenant_id}`)} className="text-xs font-semibold text-sky-600 hover:text-sky-800 transition-colors">Lihat</button>
                    <button data-testid={`tenant-flags-${t.slug}`} onClick={() => setSelected(t)} className="text-xs font-semibold text-brand-deep hover:text-gold transition-colors">Modul</button>
                    {t.status !== "suspended" ? (
                      <button data-testid={`tenant-suspend-${t.slug}`} onClick={() => setStatus(t, "suspended")} className="text-xs font-semibold text-red-500 hover:text-red-700 transition-colors">Gantung</button>
                    ) : (
                      <button data-testid={`tenant-activate-${t.slug}`} onClick={() => setStatus(t, "active")} className="text-xs font-semibold text-brand-vivid hover:text-brand-deep transition-colors">Aktifkan</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelected(null)} />
          <div className="relative bg-card rounded-2xl border border-brand-deep/15 p-6 w-full max-w-md animate-fade-up" data-testid="feature-flags-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-brand-dark">Feature Flags — {selected.name}</h3>
              <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-brand-dark"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2.5">
              {FLAGS.map((f) => (
                <label key={f} className="flex items-center justify-between bg-brand-light/60 rounded-lg px-4 py-2.5 cursor-pointer">
                  <span className="text-sm font-medium text-brand-dark capitalize">{f.replace("_", " ")}</span>
                  <button
                    data-testid={`flag-toggle-${f}`}
                    onClick={() => toggleFlag(f)}
                    className={`w-11 h-6 rounded-full transition-colors relative ${selected.feature_flags?.[f] ? "bg-brand-vivid" : "bg-slate-300"}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${selected.feature_flags?.[f] ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
